import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag, Upload, Loader2, Trash2, Eye, EyeOff,
  Image, Save, Plus, ChevronDown, ChevronUp, Package, RefreshCw,
  Receipt, ExternalLink, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Order Dashboard ──

const STATUS_COLORS = {
  PENDING: 'bg-amber-400/10 text-amber-400',
  COMPLETED: 'bg-green-400/10 text-green-400',
  PAID: 'bg-green-400/10 text-green-400',
  FAILED: 'bg-red-400/10 text-red-400',
  CANCELED: 'bg-white/5 text-white/30',
};

const OrderDashboard = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/store/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setOrders(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const pending = orders.filter(o => o.status === 'PENDING').length;
  const completed = orders.filter(o => ['COMPLETED', 'PAID'].includes(o.status)).length;

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatCents = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '';
    return `$${(amount / 100).toFixed(2)} ${currency}`;
  };

  const toggleDetail = async (orderId) => {
    if (selectedId === orderId) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(orderId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/store/orders/${orderId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setDetail(d);
        // Update the order status in the list if it changed
        if (d.status) {
          setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: d.status } : o));
        }
      }
    } catch { /* ignore */ }
    setDetailLoading(false);
  };

  return (
    <div className="mb-8" data-testid="order-dashboard">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white text-sm font-bold flex items-center gap-2">
          <Receipt size={14} className="text-green-400" /> Recent Orders
        </h3>
        <button onClick={fetchOrders} disabled={loading}
          className="p-1.5 text-white/30 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          data-testid="refresh-orders">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-4">
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-white/30 text-[9px] font-bold uppercase tracking-wider">Total</p>
          <p className="text-white text-lg font-bold" data-testid="orders-total">{orders.length}</p>
        </div>
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-amber-400/60 text-[9px] font-bold uppercase tracking-wider">Pending</p>
          <p className="text-amber-400 text-lg font-bold" data-testid="orders-pending">{pending}</p>
        </div>
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-green-400/60 text-[9px] font-bold uppercase tracking-wider">Completed</p>
          <p className="text-green-400 text-lg font-bold" data-testid="orders-completed">{completed}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-4 text-center"><Loader2 size={16} className="text-green-400 animate-spin mx-auto" /></div>
      ) : orders.length === 0 ? (
        <div className="glass-card px-4 py-6 text-center">
          <p className="text-white/20 text-xs">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {orders.map(o => (
            <div key={o.id}>
              {/* Order Row */}
              <div
                className={`glass-card px-4 py-3 flex items-center gap-3 cursor-pointer transition-all hover:border-white/10 ${selectedId === o.id ? 'border-[#E91E8D]/20 bg-[#E91E8D]/[0.02]' : ''}`}
                onClick={() => toggleDetail(o.id)}
                data-testid={`order-${o.id}`}
              >
                {o.product_image ? (
                  <img src={o.product_image} className="w-9 h-9 rounded object-cover flex-shrink-0" alt="" />
                ) : (
                  <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Package size={12} className="text-white/10" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{o.product_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-white/40 text-[10px]">{o.product_price}</span>
                    <span className="text-white/15 text-[10px]">x{o.quantity || 1}</span>
                    <span className="text-white/15 text-[10px] flex items-center gap-0.5">
                      <Clock size={8} /> {formatDate(o.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${STATUS_COLORS[o.status] || 'bg-white/5 text-white/30'}`}
                    data-testid={`order-status-${o.id}`}>
                    {o.status || 'UNKNOWN'}
                  </span>
                  <ChevronDown size={12} className={`text-white/20 transition-transform ${selectedId === o.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Detail Panel */}
              {selectedId === o.id && (
                <div className="mx-2 mt-1 mb-2 rounded-xl border border-white/5 bg-white/[0.015] overflow-hidden" data-testid={`order-detail-${o.id}`}>
                  {detailLoading ? (
                    <div className="py-6 text-center">
                      <Loader2 size={14} className="text-[#E91E8D] animate-spin mx-auto" />
                      <p className="text-white/20 text-[10px] mt-2">Fetching from Square...</p>
                    </div>
                  ) : detail?.square_details ? (
                    <div className="p-4 space-y-4">
                      {/* Square Order Info */}
                      <div className="grid grid-cols-2 gap-3">
                        <DetailField label="Square Status" value={detail.square_details.state} highlight />
                        <DetailField label="Total" value={formatCents(detail.square_details.total_amount, detail.square_details.total_currency)} />
                        <DetailField label="Order ID" value={detail.square_order_id} mono />
                        <DetailField label="Created" value={formatDate(detail.square_details.created_at)} />
                      </div>

                      {/* Line Items */}
                      {detail.square_details.line_items?.length > 0 && (
                        <div>
                          <p className="text-white/20 text-[9px] font-bold uppercase tracking-wider mb-2">Line Items</p>
                          {detail.square_details.line_items.map((li, i) => (
                            <div key={i} className="flex justify-between items-center py-1.5 border-t border-white/5">
                              <span className="text-white/60 text-[11px]">{li.name} <span className="text-white/20">x{li.quantity}</span></span>
                              <span className="text-white/50 text-[11px] font-medium">{formatCents(li.total_money?.amount, li.total_money?.currency)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Payment Details */}
                      {detail.square_details.payments?.length > 0 && (
                        <div>
                          <p className="text-white/20 text-[9px] font-bold uppercase tracking-wider mb-2">Payments</p>
                          {detail.square_details.payments.map((pay, i) => (
                            <div key={i} className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <DetailField label="Status" value={pay.status} highlight />
                                <DetailField label="Amount" value={formatCents(pay.amount?.amount, pay.amount?.currency)} />
                                <DetailField label="Method" value={
                                  pay.method === 'CARD' ? `${pay.card_brand || 'Card'} ****${pay.last_4 || ''}` :
                                  pay.method === 'WALLET' ? 'Digital Wallet' :
                                  pay.method || 'N/A'
                                } />
                                <DetailField label="Paid At" value={formatDate(pay.created_at)} />
                              </div>
                              {pay.receipt_url && (
                                <a href={pay.receipt_url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-400/5 border border-green-400/10 hover:bg-green-400/10 text-green-400 text-[10px] font-semibold transition-colors"
                                  data-testid={`receipt-link-${o.id}`}>
                                  <ExternalLink size={10} /> View Receipt
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No payments yet */}
                      {(!detail.square_details.payments || detail.square_details.payments.length === 0) && (
                        <p className="text-white/15 text-[10px] italic">No payments recorded yet — customer may not have completed checkout.</p>
                      )}

                      {/* Square Link */}
                      {detail.square_payment_link_url && (
                        <a href={detail.square_payment_link_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/50 text-[10px] transition-colors">
                          <ExternalLink size={10} /> Open checkout link
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-white/20 text-xs">{detail?.message || 'No Square details available'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DetailField = ({ label, value, highlight, mono }) => (
  <div>
    <p className="text-white/20 text-[9px] font-bold uppercase tracking-wider">{label}</p>
    <p className={`text-[11px] mt-0.5 ${highlight ? 'text-[#E91E8D] font-semibold' : 'text-white/50'} ${mono ? 'font-mono text-[10px] break-all' : ''}`}>
      {value || '—'}
    </p>
  </div>
);

// ── Showcase Manager ──

const ShowcaseManager = ({ token }) => {
  const [showcases, setShowcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/store/showcases`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setShowcases(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const create = async () => {
    const res = await fetch(`${API}/api/admin/store/showcase`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { fetch_(); toast.success('Showcase created'); }
  };

  const upload = async (id, file) => {
    setUploading(id);
    const form = new FormData(); form.append('file', file);
    const res = await fetch(`${API}/api/admin/store/showcase/${id}/upload`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    });
    if (res.ok) { fetch_(); toast.success('Image uploaded'); } else { toast.error('Upload failed'); }
    setUploading(null);
  };

  const publish = async (id) => {
    await fetch(`${API}/api/admin/store/showcase/${id}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    fetch_(); toast.success('Showcase published');
  };

  const unpublish = async (id) => {
    await fetch(`${API}/api/admin/store/showcase/${id}/unpublish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    fetch_(); toast.success('Unpublished');
  };

  const del = async (id) => {
    if (!window.confirm('Delete this showcase?')) return;
    await fetch(`${API}/api/admin/store/showcase/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetch_(); toast.success('Deleted');
  };

  const current = showcases.find(s => s.is_current && s.status === 'published');
  const archive = showcases.filter(s => !s.is_current || s.status !== 'published');

  return (
    <div className="mb-8" data-testid="showcase-manager">
      <h3 className="text-white text-sm font-bold mb-3 flex items-center gap-2">
        <Image size={14} className="text-[#E91E8D]" /> Showcase Image
      </h3>

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={e => { if (e.target.files[0] && uploadTargetRef.current) upload(uploadTargetRef.current, e.target.files[0]); }} />

      {loading ? (
        <div className="py-6 text-center"><Loader2 size={20} className="text-[#E91E8D] animate-spin mx-auto" /></div>
      ) : (
        <>
          {current && (
            <div className="glass-card p-4 mb-4 border-green-400/10">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-green-400/10 text-green-400">LIVE</span>
              </div>
              {current.image_url && <img src={current.image_url} alt="Showcase" className="w-full rounded-lg mb-3" style={{ objectFit: 'contain' }} />}
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { uploadTargetRef.current = current.id; fileRef.current?.click(); }}
                  className="px-3 py-1.5 text-xs text-white/50 bg-white/5 hover:bg-white/10 rounded-lg transition-colors" data-testid="replace-showcase">
                  {uploading === current.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} className="inline mr-1" />} Replace
                </button>
                <button onClick={() => unpublish(current.id)} className="px-3 py-1.5 text-xs text-amber-400 bg-amber-400/10 rounded-lg" data-testid="unpublish-showcase">
                  <EyeOff size={12} className="inline mr-1" /> Unpublish
                </button>
              </div>
            </div>
          )}

          <button onClick={create} className="btn-pink !text-xs mb-3" data-testid="create-showcase">
            <Plus size={12} /> New Showcase
          </button>

          {archive.length > 0 && (
            <div className="space-y-2">
              {archive.map(s => (
                <div key={s.id} className="glass-card px-4 py-3 flex items-center gap-3">
                  {s.image_url ? <img src={s.image_url} className="w-10 h-10 rounded object-cover" alt="" /> : <div className="w-10 h-10 rounded bg-white/5" />}
                  <span className="flex-1 text-white/30 text-xs">{s.status}</span>
                  <button onClick={() => { uploadTargetRef.current = s.id; fileRef.current?.click(); }}
                    className="px-2 py-1 text-[10px] text-white/40 bg-white/5 rounded">{uploading === s.id ? '...' : 'Upload'}</button>
                  <button onClick={() => publish(s.id)} className="px-2 py-1 text-[10px] text-[#E91E8D] bg-[#E91E8D]/10 rounded">Publish</button>
                  <button onClick={() => del(s.id)} className="px-2 py-1 text-[10px] text-red-400 bg-red-400/10 rounded"><Trash2 size={10} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Product Manager ──

const ProductManager = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileRef = useRef(null);
  const editorRef = useRef(null);

  // Scroll editor into view when editing starts
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editing]);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/store/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const create = async () => {
    const res = await fetch(`${API}/api/admin/store/products`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const p = await res.json(); setEditing(p); fetch_(); toast.success('Product created'); }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    const params = new URLSearchParams();
    if (editing.name) params.set('name', editing.name);
    if (editing.description !== undefined) params.set('description', editing.description);
    if (editing.price !== undefined) params.set('price', editing.price);
    if (editing.sizes) params.set('sizes', editing.sizes.join(','));
    if (editing.colors) params.set('colors', editing.colors.join(','));
    if (editing.inventory !== undefined) params.set('inventory', editing.inventory);
    if (editing.sort_order !== undefined) params.set('sort_order', editing.sort_order);
    if (editing.status) params.set('status', editing.status);

    const res = await fetch(`${API}/api/admin/store/products/${editing.id}?${params}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { setEditing(await res.json()); fetch_(); toast.success('Saved'); } else { toast.error('Save failed'); }
    setSaving(false);
  };

  const uploadImg = async (file) => {
    if (!editing) return;
    setUploading(true);
    const form = new FormData(); form.append('file', file);
    const res = await fetch(`${API}/api/admin/store/products/${editing.id}/upload`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    });
    if (res.ok) { setEditing(await res.json()); fetch_(); toast.success('Image uploaded'); } else { toast.error('Upload failed'); }
    setUploading(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await fetch(`${API}/api/admin/store/products/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (editing?.id === id) setEditing(null);
    fetch_(); toast.success('Deleted');
  };

  const update = (f, v) => setEditing(prev => ({ ...prev, [f]: v }));

  const syncCatalog = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/admin/store/catalog/sync`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const parts = [];
        if (data.pushed_to_square > 0) parts.push(`${data.pushed_to_square} pushed to Square`);
        if (data.pulled_from_square > 0) parts.push(`${data.pulled_from_square} pulled from Square`);
        if (parts.length === 0) parts.push('All products already synced');
        toast.success(parts.join(', '));
        if (data.push_errors?.length > 0) {
          data.push_errors.forEach(e => toast.error(`${e.product}: ${e.error}`));
        }
        fetch_();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Sync failed');
      }
    } catch { toast.error('Sync failed — network error'); }
    setSyncing(false);
  };

  return (
    <div data-testid="product-manager">
      <h3 className="text-white text-sm font-bold mb-3 flex items-center gap-2">
        <Package size={14} className="text-[#8B5CF6]" /> Products
      </h3>

      {loading ? (
        <div className="py-6 text-center"><Loader2 size={20} className="text-[#8B5CF6] animate-spin mx-auto" /></div>
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            <button onClick={create} className="btn-pink !text-xs" style={{ background: '#8B5CF6' }} data-testid="create-product">
              <Plus size={12} /> New Product
            </button>
            <button onClick={syncCatalog} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8D]/10 border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid="sync-square-catalog">
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {syncing ? 'Syncing...' : 'Sync Square'}
            </button>
          </div>

          {/* Editor */}
          {editing && (
            <div ref={editorRef} className="glass-card p-5 mb-5 space-y-3" data-testid="product-editor">
              <div className="flex items-center justify-between">
                <h4 className="text-white text-xs font-bold">{editing.name || 'New Product'}</h4>
                <button onClick={() => setEditing(null)} className="text-white/30 hover:text-white text-xs">Close</button>
              </div>

              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={e => e.target.files[0] && uploadImg(e.target.files[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/10 hover:border-[#8B5CF6]/30 text-white/40 text-xs" data-testid="upload-product-img">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Uploading...' : 'Upload Image'}
              </button>
              {editing.image_url && <img src={editing.image_url} alt="Preview" className="w-full rounded-lg" style={{ maxHeight: 200, objectFit: 'contain' }} />}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Name</label>
                  <input type="text" value={editing.name || ''} onChange={e => update('name', e.target.value)}
                    className="input-med w-full !text-sm" data-testid="product-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Description</label>
                  <textarea value={editing.description || ''} onChange={e => update('description', e.target.value)}
                    rows={2} className="input-med w-full !text-sm resize-none" data-testid="product-description" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Price</label>
                  <input type="text" value={editing.price || ''} onChange={e => update('price', e.target.value)}
                    placeholder="$45.00" className="input-med w-full !text-sm" data-testid="product-price" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Inventory</label>
                  <input type="number" value={editing.inventory || 0} onChange={e => update('inventory', parseInt(e.target.value) || 0)}
                    className="input-med w-full !text-sm" data-testid="product-inventory" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Sizes (comma-separated)</label>
                  <input type="text" value={(editing.sizes || []).join(', ')} onChange={e => update('sizes', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="S, M, L, XL, 2XL" className="input-med w-full !text-sm" data-testid="product-sizes" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Colors (comma-separated)</label>
                  <input type="text" value={(editing.colors || []).join(', ')} onChange={e => update('colors', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="Black, White" className="input-med w-full !text-sm" data-testid="product-colors" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Status</label>
                  <select value={editing.status || 'draft'} onChange={e => update('status', e.target.value)}
                    className="input-med w-full !text-sm" data-testid="product-status">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Sort Order</label>
                  <input type="number" value={editing.sort_order || 0} onChange={e => update('sort_order', parseInt(e.target.value) || 0)}
                    className="input-med w-full !text-sm" data-testid="product-sort" />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={save} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold transition-colors" data-testid="save-product">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
                <button onClick={() => del(editing.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold ml-auto" data-testid="delete-product">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          )}

          {/* Product List */}
          <div className="space-y-2">
            {products.map(p => (
              <div key={p.id} className="glass-card px-4 py-3 flex items-center gap-3" data-testid={`product-row-${p.id}`}>
                {p.image_url ? <img src={p.image_url} className="w-10 h-10 rounded object-cover flex-shrink-0" alt="" /> :
                  <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-white/10" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{p.name || 'Untitled'}</p>
                  <p className="text-white/20 text-[10px]">{p.price || 'No price'} &middot; <span className={p.status === 'published' ? 'text-green-400' : 'text-white/30'}>{p.status}</span>{p.square_variation_id && <span className="text-[#E91E8D] ml-1">&#9632; Square</span>}</p>
                </div>
                <button onClick={() => setEditing(p)} className="px-2 py-1 text-[10px] text-white/40 bg-white/5 rounded">Edit</button>
                <button onClick={() => del(p.id)} className="px-2 py-1 text-[10px] text-red-400 bg-red-400/10 rounded"><Trash2 size={10} /></button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── Combined Admin Tab ──

const StoreAdmin = ({ token }) => {
  return (
    <div data-testid="store-admin">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <ShoppingBag size={18} className="text-[#E91E8D]" /> Store
      </h2>
      <p className="text-white/30 text-xs mb-5">Manage the Mediterranean Club Store showcase, products, and orders.</p>
      <OrderDashboard token={token} />
      <ShowcaseManager token={token} />
      <ProductManager token={token} />
    </div>
  );
};

export default StoreAdmin;
